import { useContext, useEffect, useState, useMemo, memo } from 'react'

// @mui material components
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Icon from '@mui/material/Icon'

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox'
import SoftTypography from 'components/SoftTypography'
import SoftButton from 'components/SoftButton'

// Soft UI Dashboard PRO React example components
import DashboardLayout from 'components/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'components/Navbars/DashboardNavbar'
import Footer from 'components/Footer'
import DataTable from 'components/Tables/DataTable'
import { Uploader } from 'layouts/applications/documents/components/Uploader'

import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'

// Data
// import uploadedDocuments from "layouts/applications/documents/data/uploadedDocuments";

import LinkCell from 'layouts/applications/documents/components/LinkCell'
import { IntakeContext } from 'context/IntakeContext'
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3' // ES Modules import
import awsConfig from 'aws-exports'
import { Auth } from 'aws-amplify'
import { apiClient } from 'utils/api/apiClient'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import { Box, Button, Typography } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import ArticleIcon from '@mui/icons-material/Article'

export const Documents = memo(() => {
  const [filesToUpload, setFilesToUpload] = useState([])
  const [accessLevel, setAccessLevel] = useState('private')
  const [lastUploaded, setLastUploaded] = useState(Number(new Date()))
  const { assumedUser, loggedInUserId, isAdmin } = useContext(IntakeContext)

  //Quick and dirty method to make sure that documents are
  //loaded when the documents component is rendered
  // this - refreshes every 5 minutes
  // useEffect(() => {
  //   const shouldReload = localStorage.getItem('shouldReload')
  //   if (shouldReload !== 'false') {
  //     // Set the item to prevent further reloads
  //     localStorage.setItem('shouldReload', 'false')
  //     window.location.reload()
  //   }
  //   // Set a timeout to clear the 'shouldReload' flag after 5 minutes
  //   const timeoutId = setTimeout(() => {
  //     localStorage.removeItem('shouldReload')
  //   }, 300000)
  // }, [])

  const downloadFile = async ({ filePath }) => {
    const client = new S3Client({
      region: 'us-east-2',
      credentials: Auth.currentCredentials(),
    })
    const command = new GetObjectCommand({
      Bucket: awsConfig.aws_user_files_s3_bucket,
      Key: filePath,
    })
    return await client.send(command)
  }

  const downloadBlob = (blob, filename) => {
    const adjustedFileName =
      filename.slice(0, filename.lastIndexOf('_')) +
      filename.slice(filename.lastIndexOf('.'))
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = adjustedFileName || 'download'
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url)
        a.removeEventListener('click', clickHandler)
      }, 150)
    }
    a.addEventListener('click', clickHandler, false)
    a.click()
    return a
  }

  const triggerDocumentsRefetch = () => {
    // trigger re-fetching of documents by
    // updating a dependency of the useEffect that fetches them
    setLastUploaded(Number(new Date()))
  }

  const deleteUpload = async ({ fileKey, createdAt }) => {
    let documentsPath = `/documents/upload/${fileKey}/${createdAt}`
    if (isAdmin && assumedUser) {
      documentsPath = `/admin/documents/upload/${fileKey}/${createdAt}/${assumedUser}`
    }
    const { data } = await apiClient.delete(documentsPath)
    triggerDocumentsRefetch()
    toast.success('You have successfully deleted the document', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
    })
  }

  const streamToBlob = async (stream, mimeType) => {
    const reader = stream.getReader()
    const chunks = []

    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        break
      }

      chunks.push(value)
    }

    return new Blob(chunks, { type: mimeType })
  }

  const fetchDocumentFromApi = async ({ fileKey }) => {
    let documentsPath = `/documents/${fileKey}`
    if (isAdmin && assumedUser) {
      documentsPath = `/admin/documents/${fileKey}/${assumedUser}`
    }
    return apiClient.get(documentsPath)
  }

  const getPreview = async ({ fileKey }) => {
    const {
      data: { documentUrl },
    } = await fetchDocumentFromApi({ fileKey })
    window.open(documentUrl, '_blank')
  }

  const download = async ({ fileKey }) => {
    const {
      data: { documentUrl },
    } = await fetchDocumentFromApi({ fileKey })
    const attachmentDownloadResponse = await fetch(documentUrl)
    const blob = await attachmentDownloadResponse.blob()
    downloadBlob(blob, fileKey)
  }

  const uploadedDocuments = useMemo(() => {
    return {
      columns: [
        {
          Header: 'Document',
          accessor: 'documentLink',
          Cell: ({ value: [data] }) => {
            const { fileName, link, index } = data
            const isDisabled = ['.doc', '.docx', '.xls', '.xlsx'].includes(
              fileName.slice(fileName.lastIndexOf('.'))
            )
            return (
              <Button
                disabled={isDisabled}
                data-testid={`preview-${index}-${fileName}`}
                sx={[
                  {
                    textTransform: 'none',
                    fontSize: '15px',
                    color: '#344767',
                    fontWeight: 'bold',
                    boxShadow: 'none',
                    padding: '0px',
                  },
                  {
                    '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                      transform: 'none',
                      color: '#293852',
                      boxShadow: 'none',
                    },
                    '>span': {
                      marginBottom: '2px',
                    },
                  },
                ]}
                startIcon={<ArticleIcon />}
                href={link}
                onClick={(e) => {
                  e.preventDefault()
                  if (isDisabled) return
                  getPreview({ fileKey: fileName })
                }}
              >
                {fileName.slice(0, fileName.lastIndexOf('_')) +
                  fileName.slice(fileName.lastIndexOf('.'))}
              </Button>
            )
          },
        },
        {
          Header: 'upload date',
          accessor: 'uploadDate',
          Cell: ({ value }) => <Typography variant="body2">{value}</Typography>,
        },
        {
          Header: 'actions',
          accessor: 'documentLinkForDelete',
          Cell: ({ value: [data] }) => {
            const { fileName, link, createdAt, index } = data

            return (
              <SoftBox>
                <Button
                  size="small"
                  variant="contained"
                  sx={[
                    {
                      fontSize: '12px',
                      padding: '10px 24px 9px',
                      backgroundColor: '#344767',
                      color: '#fff',
                      fontWeight: 'normal',
                      boxShadow: 'none',
                      padding: '0px 16px',
                    },
                    {
                      '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                        transform: 'none',
                        backgroundColor: '#293852',
                        boxShadow: 'none',
                      },
                    },
                  ]}
                  startIcon={<DownloadIcon />}
                  href={link}
                  onClick={(e) => {
                    e.preventDefault()
                    download({ fileKey: fileName })
                  }}
                >
                  Download
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={[
                    {
                      fontSize: '12px',
                      padding: '10px 24px 9px',
                      backgroundColor: '#d32f2f',
                      color: '#fff',
                      fontWeight: 'normal',
                      boxShadow: 'none',
                      padding: '0px 16px',
                      marginLeft: '8px',
                    },
                    {
                      '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                        transform: 'none',
                        backgroundColor: '#c62828',
                        boxShadow: 'none',
                      },
                    },
                  ]}
                  href={link}
                  onClick={(e) => {
                    e.preventDefault()
                    deleteUpload({ fileKey: fileName, createdAt })
                  }}
                  startIcon={<DeleteIcon />}
                  data-testid={`delete-${fileName}-${index}`}
                >
                  Delete
                </Button>
              </SoftBox>
            )
          },
        },
      ],

      rows: [],
    }
  }, [])

  const [files, setFiles] = useState(uploadedDocuments)

  useEffect(() => {
    const getFiles = async () => {
      let documentsPath = '/documents'
      if (isAdmin && assumedUser) {
        documentsPath = `/admin/documents/${assumedUser}`
      }
      const { data, status } = await apiClient(documentsPath)
      if (status !== 200) {
        setFiles({ ...uploadedDocuments, rows: [] })
        return
      }
      const rows =
        data?.map(({ fileName, createdAt, updatedAt }, index) => {
          return {
            documentLinkForDelete: [{ fileName, createdAt, link: '#', index }],
            uploadDate: format(new Date(updatedAt), 'MM/d/yyyy'),
            documentLink: [{ fileName, createdAt, link: '#', index }], // [fileName, { link: '#', filePath }],
          }
        }) || []
      setFiles({ ...uploadedDocuments, rows })
    }
    getFiles()
  }, [uploadedDocuments, accessLevel, lastUploaded])

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox p={{ xs: 1, lg: 3 }}>
        <SoftBox mb={{ xs: 1, lg: 3 }}>
          <Grid container justifyContent="center">
            <Grid item xs={12} xxl={10}>
              <Card
                sx={{
                  textAlign: 'left',
                  boxShadow: 0,
                  borderRadius: 2,
                  border: '1px solid #D2D6DA',
                }}
              >
                <DataTable
                  table={files}
                  canSearch
                  entriesPerPage={{ defaultValue: 20 }}
                  alternateTableHeader="Uploaded Documents"
                  alternateTableSubHeader=""
                />
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
        <SoftBox mb={{ xs: 1, lg: 3 }}>
          <Grid container justifyContent="center">
            <Grid item xs={12} xxl={10}>
              <Card
                sx={{
                  textAlign: 'left',
                  boxShadow: 0,
                  borderRadius: 2,
                  border: '1px solid #D2D6DA',
                }}
              >
                <SoftBox p={3} lineHeight={1}>
                  <SoftBox display="flex">
                    <SoftBox textAlign="left">
                      <SoftTypography variant="h5" fontWeight="medium">
                        New Document Upload
                      </SoftTypography>
                      <SoftTypography variant="body2" fontWeight="light">
                        Upload New Document(s) for Review
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>

                  <Uploader
                    prefix={assumedUser || loggedInUserId}
                    cacheBuster={() => setLastUploaded(Number(new Date()))}
                  />
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
      {/* <Footer /> */}
    </DashboardLayout>
  )
})
Documents.displayName = 'Documents'
